"use client";

import { useEffect, useState, useRef, Fragment } from "react";
import { useParams } from "next/navigation";
import { useSession } from "next-auth/react";
import Link from "next/link";
import dynamic from "next/dynamic";
import { trackEvent } from "@/lib/posthog";
import { Navbar } from "@/components/layout/navbar";
import { Footer } from "@/components/layout/footer";
import { StarRating } from "@/components/shared/star-rating";
import { ScoreBar } from "@/components/shared/score-bar";
import { NewsSection } from "@/components/report/news-section";
import { Skeleton } from "@/components/ui/skeleton";
import type { Report } from "@/lib/types/report";
import { formatCurrency } from "@/lib/utils/format-currency";
import { InfoHint } from "@/components/compass/InfoHint";
import {
  PRICE_AT_GENERATION_LABEL,
  REPORT_OUTDATED_DAYS,
  reportFreshness,
} from "@/lib/reports/freshness";
import { formatMarketCap } from "@/lib/utils/format-number";
import { stripMarkdown } from "@/lib/utils/format";
import { IndustrySection } from "@/components/industry/IndustrySection";
import type { EmbeddedIndustryAnalysis } from "@/lib/types/industry";
import { FinancialSnapshot } from "@/components/report/financial-snapshot/FinancialSnapshot";
import type { FinancialSnapshotPayload } from "@/lib/types/financial-snapshot";

const RadarChart = dynamic(
  () => import("@/components/shared/radar-chart").then((m) => m.RadarChart),
  { ssr: false, loading: () => <Skeleton className="w-[280px] h-[280px] rounded-full mx-auto" /> }
);

const SECTION_META = [
  { key: "companySnapshot", title: "Company Snapshot", icon: "🏢", free: true },
  { key: "businessModel", title: "Business Model & Unit Economics", icon: "💼", free: true },
  { key: "competitiveMoat", title: "Competitive Moat Analysis", icon: "🏰", free: true },
  { key: "financialAnalysis", title: "Financial Analysis in Context", icon: "📊", free: false },
  { key: "growthRunway", title: "Growth Runway", icon: "🚀", free: false },
  { key: "valuationAnalysis", title: "Valuation Analysis", icon: "💰", free: false },
  { key: "keyRisks", title: "Key Risks", icon: "⚠️", free: false },
  { key: "investmentThesis", title: "Investment Thesis", icon: "🎯", free: false },
] as const;

const PAYWALL_FEATURES = [
  "Unlimited AI research reports",
  "Full 9-phase analysis for every stock",
  "PDF export & sharing",
  "Fair value estimates with DCF model",
  "Priority report generation",
];

function scoreBarColor(score: number): string {
  if (score >= 8) return "from-cgreen to-cgreen-light";
  if (score >= 6) return "from-blue to-blue-glow";
  if (score >= 4) return "from-gold to-gold";
  return "from-cred to-cred";
}

// ─── Skeleton Loading ──────────────────────────────────────────────────────

function ReportSkeleton() {
  return (
    <div className="min-h-screen bg-navy">
      <Navbar />
      <div className="max-w-[800px] mx-auto px-6 py-6">
        {/* Back nav skeleton */}
        <div className="mb-4 flex gap-3">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-20" />
        </div>

        {/* Header card skeleton */}
        <div className="bg-surface/80 border border-surface-light/30 rounded-xl p-5 mb-5">
          <div className="flex justify-between items-start mb-4">
            <div>
              <Skeleton className="h-7 w-48 mb-2" />
              <Skeleton className="h-4 w-32" />
            </div>
            <div className="text-right">
              <Skeleton className="h-6 w-24 mb-1" />
              <Skeleton className="h-3 w-20 ml-auto" />
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-navy-mid rounded-lg px-3 py-2.5">
                <Skeleton className="h-3 w-16 mb-2" />
                <Skeleton className="h-5 w-24" />
              </div>
            ))}
          </div>
          <Skeleton className="h-2 w-full mb-3" />
          <div className="flex justify-between">
            <Skeleton className="h-3 w-16" />
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-3 w-16" />
          </div>
        </div>

        {/* Score breakdown skeleton */}
        <div className="bg-surface border border-surface-light/30 rounded-xl p-5 mb-5">
          <Skeleton className="h-5 w-40 mb-4" />
          <div className="flex justify-center mb-4">
            <Skeleton className="w-[280px] h-[280px] rounded-full" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="mb-2.5">
                <div className="flex justify-between mb-1">
                  <Skeleton className="h-3 w-32" />
                  <Skeleton className="h-3 w-10" />
                </div>
                <Skeleton className="h-1.5 w-full" />
              </div>
            ))}
          </div>
        </div>

        {/* Section skeletons */}
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="bg-surface border border-surface-light/30 rounded-xl p-5 mb-2">
            <div className="flex items-center gap-2.5">
              <Skeleton className="h-5 w-5 rounded" />
              <Skeleton className="h-4 w-48" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Sticky Table of Contents ──────────────────────────────────────────────

function TableOfContents({
  activeSection,
  onClickSection,
  onClickIndustry,
  hasNews,
  hasIndustry,
  hasFinancialSnapshot,
}: {
  activeSection: number;
  onClickSection: (index: number) => void;
  onClickIndustry?: () => void;
  hasNews?: boolean;
  hasIndustry?: boolean;
  hasFinancialSnapshot?: boolean;
}) {
  // Industry section sits in the DOM between competitiveMoat (idx 2) and
  // financialAnalysis (idx 3). It's not part of SECTION_META — rendered as a
  // separate Fragment in the page — so the TOC button uses an id-anchor scroll
  // (same pattern as News) instead of the indexed sectionRefs machinery.
  return (
    <nav className="hidden lg:block fixed top-[72px] w-[200px] py-6">
      <div className="text-dim text-[10px] uppercase tracking-widest mb-3 pl-3">
        Contents
      </div>
      <div className="space-y-0.5">
        {hasFinancialSnapshot && (
          <button
            onClick={() => {
              document.getElementById("financial-snapshot")?.scrollIntoView({ behavior: "smooth" });
            }}
            className="w-full text-left px-3 py-1.5 rounded-md text-[12px] transition-all cursor-pointer bg-transparent border-none text-dim hover:text-muted-foreground"
          >
            <span className="mr-1.5">📊</span>
            Financial Snapshot
          </button>
        )}
        {SECTION_META.map((meta, i) => (
          <Fragment key={meta.key}>
            <button
              onClick={() => onClickSection(i)}
              className={`w-full text-left px-3 py-1.5 rounded-md text-[12px] transition-all cursor-pointer bg-transparent border-none ${
                activeSection === i
                  ? "text-blue bg-blue/10 font-semibold"
                  : "text-dim hover:text-muted-foreground"
              }`}
            >
              <span className="mr-1.5">{meta.icon}</span>
              {meta.title}
            </button>
            {meta.key === "competitiveMoat" && hasIndustry && (
              <button
                onClick={() => onClickIndustry?.()}
                className="w-full text-left px-3 py-1.5 rounded-md text-[12px] transition-all cursor-pointer bg-transparent border-none text-dim hover:text-muted-foreground"
              >
                <span className="mr-1.5">📈</span>
                Industry Tailwinds
              </button>
            )}
          </Fragment>
        ))}
        {hasNews && (
          <button
            onClick={() => {
              document.getElementById("news-section")?.scrollIntoView({ behavior: "smooth" });
            }}
            className="w-full text-left px-3 py-1.5 rounded-md text-[12px] transition-all cursor-pointer bg-transparent border-none text-dim hover:text-muted-foreground"
          >
            <span className="mr-1.5">📰</span>
            News
          </button>
        )}
      </div>
    </nav>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────

export function ReportViewer({ initialId }: { initialId?: string } = {}) {
  const params = useParams();
  const id = initialId ?? (params.id as string);
  const { status: authStatus } = useSession();

  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedSection, setExpandedSection] = useState(0);
  const [industryExpanded, setIndustryExpanded] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [paywallMessage, setPaywallMessage] = useState("");
  const [copied, setCopied] = useState(false);
  const [activeSection, setActiveSection] = useState(0);
  const [userPlan, setUserPlan] = useState<string>("FREE");

  // Voting state
  const [userVote, setUserVote] = useState<string | null>(null);
  const [agreeCount, setAgreeCount] = useState(0);
  const [disagreeCount, setDisagreeCount] = useState(0);
  const [votingLoading, setVotingLoading] = useState(false);

  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Phase 12 perf — fire report + vote + plan fetches IN PARALLEL.
  //
  // Was: report fetch awaited first, THEN a second useEffect fired
  // /api/user/plan once auth settled, THEN a third fired
  // /api/reports/[id]/vote once `report.id` arrived from state. Three
  // serial round-trips → ~300-500 ms of stacked latency on every
  // report view.
  //
  // Now: the vote fetch uses the URL `id` (same as the report fetch)
  // so it doesn't need to wait for state. The plan fetch is independent
  // and joins the same Promise.all. All three race; the report's own
  // setLoading toggles last (the visible "loaded" signal stays driven
  // by report arrival, not the cheaper sidecar fetches).
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const reportPromise = fetch(`/api/reports/${id}`)
        .then((r) => r.json())
        .catch(() => null);
      const votePromise = fetch(`/api/reports/${id}/vote`)
        .then((r) => r.json())
        .catch(() => null);
      // Plan fetch only when authed — but we still kick it off in
      // parallel rather than waiting on authStatus inside a separate
      // useEffect chain.
      const planPromise =
        authStatus === "authenticated"
          ? fetch("/api/user/plan")
              .then((r) => r.json())
              .catch(() => null)
          : Promise.resolve(null);

      const [reportJson, voteJson, planJson] = await Promise.all([
        reportPromise,
        votePromise,
        planPromise,
      ]);
      if (cancelled) return;

      if (!reportJson || !reportJson.success) {
        setError(reportJson?.error ?? "Failed to load report");
        setLoading(false);
        return;
      }
      setReport(reportJson.data);
      setAgreeCount(reportJson.data.agreeCount ?? 0);
      setDisagreeCount(reportJson.data.disagreeCount ?? 0);
      const score = (reportJson.data.content?.frameworkScore as { overall?: number })?.overall;
      document.title = `${reportJson.data.companyName} Analysis${score ? `: ${score}/5` : ""} | WhatsTheMoat`;

      if (voteJson?.success) {
        setUserVote(voteJson.data.userVote);
        // Vote endpoint returns canonical agree/disagree counts;
        // these overwrite the report's snapshot for freshness.
        setAgreeCount(voteJson.data.agreeCount);
        setDisagreeCount(voteJson.data.disagreeCount);
      }

      if (planJson?.success) {
        setUserPlan(planJson.plan);
      }

      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [id, authStatus]);


  async function handleVote(vote: "AGREE" | "DISAGREE") {
    if (authStatus !== "authenticated" || !report) return;
    setVotingLoading(true);
    try {
      const res = await fetch(`/api/reports/${report.id}/vote`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ vote }),
      });
      const json = await res.json();
      if (json.success) {
        setUserVote(json.data.userVote);
        setAgreeCount(json.data.agreeCount);
        setDisagreeCount(json.data.disagreeCount);
      }
    } catch { /* ignore */ }
    setVotingLoading(false);
  }

  // IntersectionObserver for sticky TOC highlighting
  useEffect(() => {
    if (!report) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const idx = sectionRefs.current.indexOf(entry.target as HTMLDivElement);
            if (idx !== -1) setActiveSection(idx);
          }
        }
      },
      { rootMargin: "-80px 0px -60% 0px", threshold: 0 }
    );

    sectionRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, [report]);

  function scrollToSection(index: number) {
    const el = sectionRefs.current[index];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      setExpandedSection(index);
    }
  }

  if (loading) return <ReportSkeleton />;

  if (error || !report) {
    return (
      <div className="min-h-screen bg-navy">
        <Navbar />
        <div className="flex flex-col items-center justify-center pt-32 gap-4">
          <p className="text-cred text-sm">{error ?? "Report not found"}</p>
          <Link href="/search" className="text-blue text-sm hover:underline">
            ← Back to search
          </Link>
        </div>
      </div>
    );
  }

  const isPro = userPlan === "PRO" || userPlan === "PREMIUM";
  const canAccess = (free: boolean) => free || isPro;

  function openPaywall(msg?: string, trigger: string = "section") {
    setPaywallMessage(msg ?? "");
    setShowPaywall(true);
    // Funnel: the moment a free user hits the Pro wall — the conversion signal between
    // report_generated and pricing_view. `trigger` distinguishes a locked SECTION click
    // from the PDF / refresh Pro-only actions.
    trackEvent("paywall_shown", { surface: "stock_report", trigger });
  }

  const { content } = report;
  const industryAnalysis =
    (content as { industryAnalysis?: EmbeddedIndustryAnalysis }).industryAnalysis ?? null;
  const score = content.frameworkScore;
  const bd = score.breakdown;
  const fv = content.valuationAnalysis.fairValueRange;
  const curr = report.currency as "INR" | "USD" | "AUD";

  // Range-based verdict — LLM-written currentVsFairValue misclassifies prices within the band.
  // Matches OG computeVerdict: >5% above high = OVERVALUED, >5% below low = UNDERVALUED.
  const valuationVerdict: "UNDERVALUED" | "OVERVALUED" | "FAIR" =
    report.currentPrice > 0 && fv.low > 0 && fv.high > 0
      ? report.currentPrice < fv.low * 0.95
        ? "UNDERVALUED"
        : report.currentPrice > fv.high * 1.05
          ? "OVERVALUED"
          : "FAIR"
      : "FAIR";

  // Extract stored news from report content JSON (new reports have this)
  const contentRaw = content as Record<string, unknown>;
  const storedNewsItems = Array.isArray(contentRaw.newsItems) ? contentRaw.newsItems : undefined;
  const hasNews = storedNewsItems && storedNewsItems.length > 0;

  // Fair value bar positioning
  const priceMin = fv.low * 0.7;
  const priceMax = fv.high * 1.3;
  const range = priceMax - priceMin;
  const fvLeftPct = ((fv.low - priceMin) / range) * 100;
  const fvRightPct = 100 - ((fv.high - priceMin) / range) * 100;
  const currentPct = ((report.currentPrice - priceMin) / range) * 100;

  // RM-W3-12: the report is a snapshot. Its price is the price at generation, never "current".
  const freshness = reportFreshness(report.dataAsOf, report.generatedAt, new Date());

  const radarAxes = [
    { label: "Business Quality", value: bd.businessQuality.score },
    { label: "Competitive Moat", value: bd.competitiveMoat.score },
    { label: "Financial Health", value: bd.financialHealth.score },
    { label: "Growth Runway", value: bd.growthRunway.score },
    { label: "Valuation", value: bd.valuationAttractiveness.score },
    { label: "Risk Profile", value: bd.riskProfile.score },
  ];

  return (
    <div className="min-h-screen bg-navy">
      <Navbar />

      {/* Sticky Table of Contents — desktop only */}
      <TableOfContents
        activeSection={activeSection}
        onClickSection={scrollToSection}
        onClickIndustry={() => {
          setIndustryExpanded(true);
          setTimeout(() => {
            document
              .getElementById("industry-tailwinds")
              ?.scrollIntoView({ behavior: "smooth" });
          }, 50);
        }}
        hasNews={hasNews}
        hasIndustry={!!industryAnalysis}
        hasFinancialSnapshot={!!(content as { financialSnapshot?: FinancialSnapshotPayload }).financialSnapshot}
      />

      <div className="max-w-[800px] mx-auto px-6 py-6 lg:ml-[220px] lg:max-w-[780px]">
        {/* Back nav */}
        <div className="mb-4 flex gap-3">
          <Link href="/search" className="text-muted-foreground text-sm hover:text-foreground transition no-underline">
            ← New search
          </Link>
          <Link href="/dashboard" className="text-muted-foreground text-sm hover:text-foreground transition no-underline">
            Dashboard
          </Link>
        </div>

        {/* Summary Header Card — Glassmorphism */}
        <div
          className="rounded-xl p-5 mb-5 border border-blue/25"
          style={{
            background: "linear-gradient(135deg, rgba(20,27,45,0.8) 0%, rgba(10,14,26,0.75) 100%)",
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)",
          }}
        >
          <div className="flex justify-between items-start mb-4">
            <div>
              <div className="flex items-center gap-2.5 mb-1 flex-wrap">
                <h1 className="text-foreground text-[28px] font-serif m-0">
                  {report.companyName}
                </h1>
                <span className="bg-cgreen/20 text-cgreen px-2 py-0.5 rounded-md text-[11px] font-semibold tracking-wide">
                  {report.exchange}: {report.ticker}
                </span>
              </div>
              <div className="text-muted-foreground text-[13px]">
                {report.sector}
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="flex items-center gap-1.5">
                <StarRating score={score.overall} size="text-xl" />
                <span
                  className="text-gold font-extrabold text-[22px] font-tabular"
                  style={{
                    textShadow: "0 0 20px rgba(245,158,11,0.3)",
                  }}
                >
                  {score.overall}/5
                </span>
              </div>
              <div className="text-muted-foreground text-[11px] mt-0.5">
                Framework Score
              </div>
            </div>
          </div>

          {/* Metric boxes */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div className="bg-navy-mid/60 rounded-lg px-3 py-2.5">
              <div className="text-dim text-[10px] uppercase tracking-wide mb-1 flex items-center gap-1">
                Price at generation
                <InfoHint
                  label="price at generation"
                  text={`The share price when this report was generated (${freshness.dateLabel}). It is stored with the report and is not a live quote.`}
                />
              </div>
              <div className="text-foreground font-bold text-base font-tabular">
                {formatCurrency(report.currentPrice, curr)}
              </div>
            </div>
            <div className="bg-navy-mid/60 rounded-lg px-3 py-2.5">
              <div className="text-dim text-[10px] uppercase tracking-wide mb-1">Market Cap</div>
              <div className="text-foreground font-bold text-base font-tabular">
                {formatMarketCap(report.marketCap, curr)}
              </div>
            </div>
            <div className="bg-navy-mid/60 rounded-lg px-3 py-2.5">
              <div className="text-dim text-[10px] uppercase tracking-wide mb-1">Fair Value Range</div>
              <div className="text-foreground font-bold text-base font-tabular">
                {formatCurrency(fv.low, curr)} to {formatCurrency(fv.high, curr)}
              </div>
              <div className={`text-[11px] mt-0.5 ${
                valuationVerdict === "UNDERVALUED" ? "text-cgreen" :
                valuationVerdict === "OVERVALUED" ? "text-cred" :
                "text-gold"
              }`}>
                {valuationVerdict === "UNDERVALUED" ? "Undervalued" :
                 valuationVerdict === "OVERVALUED" ? "Overvalued" :
                 "~Fair Value"}
              </div>
            </div>
            <div className="bg-navy-mid/60 rounded-lg px-3 py-2.5">
              <div className="text-dim text-[10px] uppercase tracking-wide mb-1">Methodology</div>
              <div className="text-foreground font-bold text-base">{fv.methodology}</div>
              <div className="text-dim text-[11px] mt-0.5">{freshness.dataAsOfLabel}</div>
            </div>
          </div>

          {freshness.isOutdated && (
            <div
              data-testid="report-outdated"
              className="mb-4 bg-gold/10 border border-gold/30 rounded-lg px-3 py-2 flex items-center gap-2"
            >
              <span className="text-gold text-[12px]">
                This report&apos;s data is {freshness.ageDays} days old. Consider generating a fresh analysis.
              </span>
              <InfoHint
                label="an outdated report"
                text={`Reports whose data is more than ${REPORT_OUTDATED_DAYS} days old are marked outdated: prices, results and news may have moved since.`}
              />
            </div>
          )}

          {/* Fair Value Bar */}
          <div className="mb-3">
            {Array.isArray((contentRaw as { warnings?: string[] }).warnings) &&
              (contentRaw as { warnings: string[] }).warnings.map((w, i) => (
                <div
                  key={i}
                  className="mb-2 rounded-md border border-gold/30 bg-gold/5 px-3 py-2 text-[11px] text-gold/90"
                >
                  ⚠ {w}
                </div>
              ))}
            <div className="flex justify-between mb-1">
              <span className="text-dim text-[11px]">Undervalued</span>
              <span className="text-dim text-[11px]">Overvalued</span>
            </div>
            <div className="bg-navy-mid rounded h-2 relative">
              <div
                className="absolute top-0 bottom-0 bg-cgreen/20 rounded"
                style={{ left: `${fvLeftPct}%`, right: `${fvRightPct}%` }}
              />
              <div
                className="absolute top-[-3px] w-3.5 h-3.5 bg-blue rounded-full border-2 border-foreground"
                style={{ left: `${Math.min(Math.max(currentPct, 2), 98)}%`, transform: "translateX(-50%)" }}
              />
            </div>
            <div className="flex justify-between mt-1">
              <span className="text-cgreen text-[11px] font-tabular">
                {formatCurrency(fv.low, curr)}
              </span>
              <span className="text-muted-foreground text-[11px] font-semibold font-tabular">
                {formatCurrency(report.currentPrice, curr)} ({PRICE_AT_GENERATION_LABEL})
              </span>
              <span className="text-cgreen text-[11px] font-tabular">
                {formatCurrency(fv.high, curr)}
              </span>
            </div>
          </div>

          {/* Footer row */}
          <div className="flex items-center gap-2 pt-3 border-t border-surface-light/30 flex-wrap">
            <span className="text-dim text-[11px]" data-testid="report-generated">Generated: {freshness.generatedLabel}</span>
            <span className="text-dim text-[11px]">&bull;</span>
            <span className="text-dim text-[11px] inline-flex items-center gap-1" data-testid="report-data-as-of">
              {freshness.dataAsOfLabel}
              <InfoHint label="the data date" text={freshness.tooltip} />
            </span>
            <div className="ml-auto flex gap-2">
              <button
                onClick={() => {
                  if (!isPro) {
                    openPaywall("Upgrade to Pro to download PDF reports", "pdf");
                    return;
                  }
                  window.open(`/api/reports/${id}/pdf`, "_blank");
                }}
                className="text-muted-foreground text-xs border border-surface-light/30 rounded-md px-3 py-1.5 hover:bg-surface/50 transition cursor-pointer bg-transparent"
              >
                📄 PDF {!isPro && <span className="text-gold text-[10px]">PRO</span>}
              </button>
              <button
                onClick={() => {
                  if (!isPro) {
                    openPaywall("Upgrade to Pro to refresh reports with latest data", "refresh");
                    return;
                  }
                  window.location.href = `/report/generate?ticker=${report.ticker}&exchange=${report.exchange}`;
                }}
                className="text-muted-foreground text-xs border border-surface-light/30 rounded-md px-3 py-1.5 hover:bg-surface/50 transition cursor-pointer bg-transparent"
              >
                🔄 Refresh {!isPro && <span className="text-gold text-[10px]">PRO</span>}
              </button>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/stocks/${report.exchange}/${report.ticker}`);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}
                className="text-muted-foreground text-xs border border-surface-light/30 rounded-md px-3 py-1.5 hover:bg-surface/50 transition cursor-pointer bg-transparent"
              >
                {copied ? "✓ Copied!" : "🔗 Share"}
              </button>
            </div>
          </div>
        </div>

        {/* Score Breakdown — Radar Chart + Bars */}
        <div className="bg-surface border border-surface-light/30 rounded-xl p-5 mb-5">
          <h3 className="text-foreground text-base font-serif mb-4">
            Score Breakdown
          </h3>

          {/* Radar Chart (primary) */}
          <RadarChart axes={radarAxes} />

          {/* Horizontal bars (secondary) */}
          <div className="mt-5 pt-4 border-t border-surface-light/20">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
              <ScoreBar label={bd.businessQuality.label} value={bd.businessQuality.score} color={scoreBarColor(bd.businessQuality.score)} />
              <ScoreBar label={bd.competitiveMoat.label} value={bd.competitiveMoat.score} color={scoreBarColor(bd.competitiveMoat.score)} />
              <ScoreBar label={bd.financialHealth.label} value={bd.financialHealth.score} color={scoreBarColor(bd.financialHealth.score)} />
              <ScoreBar label={bd.growthRunway.label} value={bd.growthRunway.score} color={scoreBarColor(bd.growthRunway.score)} />
              <ScoreBar label={bd.valuationAttractiveness.label} value={bd.valuationAttractiveness.score} color={scoreBarColor(bd.valuationAttractiveness.score)} />
              <ScoreBar label={bd.riskProfile.label} value={bd.riskProfile.score} color={scoreBarColor(bd.riskProfile.score)} />
            </div>
          </div>

          <p className="text-muted-foreground text-xs mt-3 leading-relaxed">
            {stripMarkdown(score.summary)}
          </p>

          {/* Agree/Disagree Voting */}
          <div className="mt-4 pt-4 border-t border-surface-light/20">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="text-dim text-[12px]">
                {agreeCount + disagreeCount > 0 ? (
                  <>
                    <span className="text-foreground font-semibold">
                      {Math.round((agreeCount / (agreeCount + disagreeCount)) * 100)}%
                    </span>{" "}
                    of {agreeCount + disagreeCount} voter{agreeCount + disagreeCount !== 1 ? "s" : ""} agree with this rating
                  </>
                ) : (
                  "Be the first to rate this analysis"
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleVote("AGREE")}
                  disabled={votingLoading || authStatus !== "authenticated"}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition border cursor-pointer ${
                    userVote === "AGREE"
                      ? "bg-cgreen/20 border-cgreen/40 text-cgreen"
                      : "bg-transparent border-surface-light/30 text-muted-foreground hover:border-cgreen/40 hover:text-cgreen"
                  } disabled:opacity-40 disabled:cursor-default`}
                  title={authStatus !== "authenticated" ? "Login to vote" : ""}
                >
                  <span>👍</span> Agree {agreeCount > 0 && <span className="font-tabular">{agreeCount}</span>}
                </button>
                <button
                  onClick={() => handleVote("DISAGREE")}
                  disabled={votingLoading || authStatus !== "authenticated"}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition border cursor-pointer ${
                    userVote === "DISAGREE"
                      ? "bg-cred/20 border-cred/40 text-cred"
                      : "bg-transparent border-surface-light/30 text-muted-foreground hover:border-cred/40 hover:text-cred"
                  } disabled:opacity-40 disabled:cursor-default`}
                  title={authStatus !== "authenticated" ? "Login to vote" : ""}
                >
                  <span>👎</span> Disagree {disagreeCount > 0 && <span className="font-tabular">{disagreeCount}</span>}
                </button>
              </div>
            </div>
          </div>
        </div>

        {(content as { financialSnapshot?: FinancialSnapshotPayload }).financialSnapshot && (
          <div id="financial-snapshot">
            <FinancialSnapshot
              payload={(content as { financialSnapshot: FinancialSnapshotPayload }).financialSnapshot}
              currency={curr === "INR" ? "₹" : curr === "USD" ? "$" : curr === "AUD" ? "A$" : curr}
              userTier={isPro ? (userPlan === "PREMIUM" ? "premium" : "pro") : "free"}
            />
          </div>
        )}

        {/* Report Sections — Animated expand/collapse.
            A11y: each header is a real <button> (keyboard-operable, with
            aria-expanded/aria-controls) wrapping an <h2>, and the
            collapsible body is a labelled region. Locked sections keep the
            exact same paywall behavior — the button opens the paywall
            modal and the (redacted teaser-string) body stays CSS-collapsed
            because `expanded` requires `isExpanded && accessible`. */}
        {SECTION_META.map((meta, i) => {
          const section = content[meta.key];
          const isExpanded = expandedSection === i;
          const accessible = canAccess(meta.free);
          const isOpen = isExpanded && accessible;
          const headerId = `report-section-header-${meta.key}`;
          const panelId = `report-section-panel-${meta.key}`;
          return (
            <Fragment key={meta.key}>
              <div
                ref={(el) => { sectionRefs.current[i] = el; }}
                className={`bg-surface border rounded-xl p-5 mb-2 transition scroll-mt-20 ${
                  isOpen ? "border-blue/40" : "border-surface-light/30"
                }`}
              >
                {/* Heading wraps the button (APG disclosure pattern) — gives
                    SR users both the document outline AND a keyboard-
                    operable toggle. h2 + button styles are reset so the
                    visual rendering is identical to the old div row. */}
                <h2 id={headerId} className="m-0">
                  <button
                    type="button"
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    onClick={() => {
                      if (!accessible) {
                        openPaywall();
                        return;
                      }
                      setExpandedSection(isExpanded ? -1 : i);
                    }}
                    className="w-full flex items-center justify-between cursor-pointer bg-transparent border-none p-0 text-left"
                  >
                    <span className="flex items-center gap-2.5">
                      <span className="text-lg" aria-hidden="true">{meta.icon}</span>
                      <span className="text-foreground font-serif text-base">
                        {meta.title}
                      </span>
                      {!accessible && (
                        <span className="text-xs bg-gold/20 text-gold px-2 py-0.5 rounded">
                          🔒 PRO
                        </span>
                      )}
                    </span>
                    {accessible ? (
                      <span
                        aria-hidden="true"
                        className={`text-dim text-lg transition-transform duration-300 ${
                          isExpanded ? "rotate-180" : ""
                        }`}
                      >
                        ▾
                      </span>
                    ) : (
                      <span className="text-gold text-xs">Unlock →</span>
                    )}
                  </button>
                </h2>
                {/* Animated content area — aria-hidden while collapsed so
                    screen readers don't read the visually-hidden body. */}
                <div
                  id={panelId}
                  role="region"
                  aria-labelledby={headerId}
                  aria-hidden={!isOpen}
                  className={`section-collapsible ${isOpen ? "expanded" : ""}`}
                >
                  <div className="section-inner">
                    <div className="section-content">
                      <div className="mt-4 pt-4 border-t border-surface-light/30">
                        {section.content.split("\n\n").map((p: string, j: number) => (
                          <p
                            key={j}
                            className="text-muted-foreground text-sm leading-[1.7] mb-3 last:mb-0"
                          >
                            {p}
                          </p>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {meta.key === "competitiveMoat" && industryAnalysis && (
                <IndustrySection
                  data={industryAnalysis}
                  companyName={report.companyName}
                  isExpanded={industryExpanded}
                  onToggle={() => setIndustryExpanded((v) => !v)}
                />
              )}
            </Fragment>
          );
        })}

        {/* News Section — reads from stored content, falls back to API for older reports */}
        <NewsSection
          ticker={report.ticker}
          companyName={report.companyName}
          exchange={report.exchange}
          storedNews={storedNewsItems}
        />

        {/* Disclaimer */}
        <div className="mt-6 p-4 bg-navy-mid rounded-lg border border-surface-light/30">
          <p className="text-dim text-[11px] leading-relaxed m-0">
            ⚠️ <strong>Disclaimer:</strong> This analysis is generated by looking at all the information publicly available. It is not investment
            advice. The framework score is not a buy, sell, or hold
            recommendation. Always conduct your own research and consult a
            qualified financial advisor before making investment decisions. Past
            performance does not guarantee future results.
          </p>
        </div>
      </div>

      <Footer />

      {/* Paywall Modal */}
      {showPaywall && (
        <div
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-[200] backdrop-blur-sm"
          onClick={() => setShowPaywall(false)}
        >
          <div
            className="bg-navy border border-blue/25 rounded-xl p-5 max-w-[440px] w-full mx-4 text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-[40px] mb-3">🔒</div>
            <h2 className="text-foreground text-[24px] font-serif mb-2">
              {paywallMessage || "Unlock Full Report"}
            </h2>
            <p className="text-muted-foreground text-sm leading-relaxed mb-5">
              {paywallMessage
                ? "Pro members get unlimited reports, PDF downloads, refresh, and full analysis access."
                : "Get access to Financial Analysis, Growth Runway, Valuation Model, Risk Assessment, and Investment Thesis: the sections that matter most."}
            </p>
            <div className="bg-navy-mid rounded-xl p-4 mb-5">
              <div className="text-gold text-xs font-semibold uppercase tracking-widest mb-1">
                PRO PLAN
              </div>
              <div className="text-foreground text-[32px] font-extrabold font-tabular">
                $9.99
                <span className="text-base font-normal text-muted-foreground">
                  /month
                </span>
              </div>
              <div className="text-dim text-[13px]">
                or $99/year (save 17%)
              </div>
            </div>
            <div className="text-left mb-5">
              {PAYWALL_FEATURES.map((f, i) => (
                <div key={i} className="flex items-center gap-2 py-1">
                  <span className="text-cgreen">✓</span>
                  <span className="text-muted-foreground text-[13px]">{f}</span>
                </div>
              ))}
            </div>
            <Link
              href="/pricing"
              onClick={() =>
                trackEvent("upgrade_clicked", { surface: "stock_report", destination: "/pricing" })
              }
              className="block w-full bg-gradient-to-br from-blue to-blue-glow text-white font-semibold text-[15px] py-3 rounded-lg hover:opacity-90 transition no-underline text-center"
            >
              Start Pro ($9.99/month)
            </Link>
            <p className="text-dim text-[11px] mt-2.5 mb-0">
              Cancel anytime &bull; 7-day money-back guarantee
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
